import {
  ArrowLeftRight,
  Banknote,
  Bell,
  Bike,
  Boxes,
  ClipboardCheck,
  ClipboardList,
  Coins,
  Factory,
  Hash,
  HandCoins,
  History,
  Hourglass,
  LayoutDashboard,
  Package,
  PackageOpen,
  PackagePlus,
  Receipt,
  Ruler,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Store,
  TrendingUp,
  UserCog,
  Wallet,
  Wheat,
} from "lucide-react";

import type { Permission } from "@artisancode/api-types";
import type { LucideIcon } from "lucide-react";

/** Who may open a nav entry. Entries the user can't open are hidden. */
export interface NavAccess {
  permission?: Permission;
  roles?: string[];
  /** When set, allowed if ANY rule passes — overrides permission/roles above. */
  anyOf?: { permission?: Permission; roles?: string[] }[];
}

export interface NavItem extends NavAccess {
  to: string;
  label: string;
  icon: LucideIcon;
}

export const dashboardNav: NavItem = {
  to: "/dashboard",
  label: "Dashboard",
  icon: LayoutDashboard,
};

export const activityLogNav: NavItem = {
  to: "/activity-logs",
  label: "Riwayat Aktivitas",
  icon: History,
  permission: "activity_logs.view",
};

// Workspace-scoped data: master data (products, raw materials, units, unit
// conversions, riders) and this store's own settings both vary per
// workspace, so they live together in one group — separate from
// "Pengaturan Global" below, which is deliberately cross-workspace.
export const workspaceDataNav: NavItem[] = [
  {
    to: "/master/products",
    label: "Produk",
    icon: Package,
    permission: "products.view",
  },
  {
    to: "/master/raw-materials",
    label: "Bahan Baku",
    icon: Wheat,
    permission: "raw_materials.view",
  },
  { to: "/master/uoms", label: "Satuan", icon: Ruler, permission: "uoms.view" },
  {
    to: "/master/unit-conversions",
    label: "Konversi Satuan",
    icon: ArrowLeftRight,
    permission: "unit_conversions.view",
  },
  {
    to: "/master/riders",
    label: "Rider",
    icon: Bike,
    permission: "riders.view",
  },
  {
    to: "/settings/workspace/users",
    label: "Pengguna & Staf",
    icon: UserCog,
    permission: "users.view",
  },
  { to: "/settings/workspace", label: "Profil Toko Ini", icon: Store },
  {
    to: "/settings/workspace/gaji-bonus-rider",
    label: "Gaji & Bonus Rider",
    icon: Coins,
    permission: "workspaces.view",
  },
  // Disabled for now — depends on notification_channel (see rest.ts).
  // { to: "/settings/workspace/notifications", label: "Notifikasi Workspace", icon: Bell, permission: "workspaces.view" },
  {
    to: "/settings/workspace/number-sequence",
    label: "Penomoran Transaksi",
    icon: Hash,
    permission: "workspaces.view",
  },
];

export const riderPendingPaymentNav: NavItem = {
  to: "/rider-pending-payment",
  label: "Rider Belum Dibayar",
  icon: Hourglass,
  permission: "rider_sales.view",
};

export const riderStockNav: NavItem = {
  to: "/rider-stocks",
  label: "Stok di Rider",
  icon: PackageOpen,
  permission: "stocks.view",
};

export const riderPayrollNav: NavItem = {
  to: "/rider-payrolls",
  label: "Penggajian Rider",
  icon: Banknote,
  permission: "rider_payrolls.view",
};

export const salesNav: NavItem[] = [
  {
    to: "/sales",
    label: "Penjualan Toko",
    icon: ShoppingCart,
    permission: "sales.view",
  },
  {
    to: "/rider-sales",
    label: "Penjualan Rider",
    icon: Bike,
    permission: "rider_sales.view",
  },
  riderStockNav,
  riderPendingPaymentNav,
  riderPayrollNav,
];

export const financeNav: NavItem[] = [
  {
    to: "/cash-report",
    label: "Kas",
    icon: Wallet,
    permission: "cash_report.view",
  },
  {
    to: "/gross-profit",
    label: "Laba Kotor",
    icon: TrendingUp,
    permission: "gross_profit_report.view",
  },
  {
    to: "/expenses",
    label: "Pengeluaran",
    icon: Receipt,
    permission: "expenses.view",
  },
  {
    to: "/receivables",
    label: "Piutang",
    icon: HandCoins,
    permission: "receivables.view",
  },
];

export const inventoryNav: NavItem[] = [
  {
    to: "/stocks",
    label: "Daftar Stok",
    icon: Boxes,
    permission: "stocks.view",
  },
  {
    to: "/opening-stocks",
    label: "Stok Awal",
    icon: PackagePlus,
    permission: "opening_stocks.view",
  },
  {
    to: "/stock-opname/raw-materials",
    label: "Opname Bahan Baku",
    icon: ClipboardCheck,
    permission: "stock_opnames.view",
  },
  {
    to: "/stock-opname/products",
    label: "Opname Produk",
    icon: ClipboardList,
    permission: "stock_opnames.view",
  },
  {
    to: "/stock-adjustments",
    label: "Penyesuaian Stok",
    icon: SlidersHorizontal,
    permission: "stock_adjustments.view",
  },
  {
    to: "/repacking",
    label: "Repacking",
    icon: PackageOpen,
    permission: "stock_transformations.view",
  },
  {
    to: "/production",
    label: "Produksi",
    icon: Factory,
    permission: "stock_transformations.view",
  },
];

export const globalSettingsNav: NavItem[] = [
  {
    to: "/settings/workspaces",
    label: "Semua Workspace",
    icon: Boxes,
    permission: "workspaces.view",
  },
  {
    to: "/settings/users",
    label: "Pengguna & Staf",
    icon: UserCog,
    permission: "users.view",
  },
  {
    to: "/settings/roles",
    label: "Roles & Hak Akses",
    icon: ShieldCheck,
    permission: "roles.view",
  },
  {
    to: "/settings/notifications",
    label: "Notifikasi",
    icon: Bell,
    anyOf: [
      { roles: ["Developer", "Franchisor"] },
      { permission: "notification_channels.view" },
    ],
  },
];
